import { Button, IconText } from '@distribusion/owl-frontend-ui'
import cn from 'classnames'
import React, { ForwardedRef, forwardRef, ReactElement } from 'react'

import ConnectionCalendar from '@components/ConnectionCalendar'
import DirectOnlyFilter from '@components/DirectOnlyFilter'
import JourneyListTitle from '@components/JourneyList/Header/Title'
import Sort from '@components/JourneyList/Sort'
import useIsMobile from '@hooks/useIsMobile'
import useIsTablet from '@hooks/useIsTablet'
import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { withPluginSlot } from '@plugins/index'
import { useSettings } from '@queries/settings'
import { useParams } from '@stores/params'

export interface JourneyListHeaderProps {
  twoWayConnections: boolean
  outbound?: Connection | null
  onFiltersOpened: () => void
  isLoading: boolean
}

const Header = (props: JourneyListHeaderProps, ref: ForwardedRef<HTMLHeadingElement>): ReactElement => {
  const { twoWayConnections, outbound, onFiltersOpened, isLoading } = props

  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const [{ sorting, searchCalendar, filter }] = useSettings()
  const [{ bookingId }] = useParams()
  const showSort = sorting.enabled
  const showFilter = (isMobile || isTablet) && filter.enabled
  const showControlPanel = showSort || showFilter
  const showSearchCalendar = searchCalendar.enabled && !bookingId

  const showDirectOnlyFilter = filter.directOnlyInlineHeaderView

  const filterButton = (
    <Button variant="text" color="secondary" onClick={onFiltersOpened} data-tag="journey-list-filters-button">
      <IconText label={t('journeyList.filters.title')} iconName="settings" />
    </Button>
  )

  return (
    <div className={cn(bem('journey-list', 'header', { expanded: !showControlPanel }), 'column')}>
      <h3
        ref={ref}
        className={bem('journey-list', 'header-title', { minimized: !showSearchCalendar && showControlPanel })}
      >
        <JourneyListTitle twoWayConnections={twoWayConnections} outbound={outbound} />
      </h3>
      {showSearchCalendar && <ConnectionCalendar outbound={outbound} isLoading={isLoading} />}
      {showControlPanel && (
        <div className="journey-list__header-control-panel column row-lg items-center">
          <div
            className={cn('row items-center w-100', {
              'journey-list__header-filter-enabled': showDirectOnlyFilter,
              'space-between': !showDirectOnlyFilter,
            })}
          >
            {showDirectOnlyFilter && <DirectOnlyFilter />}
            {showSort && <Sort showTogetherWithDirectOnlyFilter={showDirectOnlyFilter} />}
            {showFilter && filterButton}
          </div>
        </div>
      )}
    </div>
  )
}

export default withPluginSlot(forwardRef(Header), 'JourneyListHeader')
